import MonitorIcon from '@mui/icons-material/Monitor';
import WordpressIcon from '../../components/SvgIcons/WordpressIcon';

export const genptTags = {
    unauthenticated: {
        title: "Unauth",
        bg_color: "var(--grey2)",
    },
    authenticated: {
        title: "Auth",
        bg_color: "var(--grey1)",
    },
    quick: {
        title: "Express",
        bg_color: "var(--grey1)",
    },
    normal: {
        title: "Site-wide",
        bg_color: "var(--grey1)",
    },
};

// genpt test status enum
export const genptTestStatus = {
    inprogress: {
        title: "In Progress",
        bg_color: "var(--functional-blue-600)",
    },
    completed: {
        title: "Completed",
        bg_color: "var(--secondary-700)",
    },
    failed: {
        title: "Failed",
        bg_color: "var(--functional-red)",
    },
};

export const genptFindingSeverity = {
    critical: {
        title: "Critical",
        color: "white",
        bg_color: "var(--functional-red-900)",
    },
    high: {
        title: "High",
        color: "white",
        bg_color: "var(--functional-red-700)",
    },
    medium: {
        title: "Medium",
        color: "white",
        bg_color: "var(--functional-orange)",
    },
    low: {
        title: "Low",
        color: "black",
        bg_color: "var(--functional-yellow)",
    },
    info: {
        title: "Informational",
        color: "black",
        bg_color: "var(--functional-blue-50)",
    },
};

export const genptFindingSeverityGraphColor = {
    critical: {
        title: "Critical",
        color: "var(--functional-red-900)",
    },
    high: {
        title: "High",
        color: "var(--functional-red-700)",
    },
    medium: {
        title: "Medium",
        color: "var(--functional-orange)",
    },
    low: {
        title: "Low",
        color: "var(--functional-yellow)",
    },
    info: {
        title: "Informational",
        color: "var(--functional-blue-50)",
    },
};


export const gentPTAssetTypeMap = {
    web_app: {
        title: "Web App",
        shortTitle: "Web",
        icon: (<MonitorIcon className={"text-primary-500 align-top"} />)
    },
    wordpress: {
        title: "WordPress",
        icon: <WordpressIcon />
    }
}

export const genptAssetToTemplateCountMap = {
    web_app: {
        unauthenticated: 4928,
    },
    wordpress: {
        unauthenticated: 10442,
        authenticated: 10584,
    }
}

export const findingStatusColors = {
    new: {
        name: "New",
        color: "var(--functional-red-900)",
        description: "Initial status of the newly discovered vulnerability."
    },
    existing: {
        name: "Existing",
        color: "var(--functional-red-700)",
        description: "Known issue under investigation or resolution."
    },
    reopened: {
        name: "Re-opened",
        color: "var(--functional-orange)",
        description: "Previously resolved issue that has reoccurred."
    },
    fixed: {
        name: "Fixed",
        color: "var(--success-green)",
        description: "Issue resolved with no further action required."
    },
}

export const testActivityMaps = {
    unauthenticated: {
        name: "Unauthenticated Testing",
        sub_activities: ["Verifying Core Integrity", "Assessing Plugin Review", "Evaluating Theme Safety"]
    },
    authenticated: {
        name: "Authenticated Testing",
        sub_activities: ["In-depth Core Examination", "Authenticated Plugin Checks", "Thorough Theme Inspection"]
    },
    exposures: {
        name: "Sensitive Data Exposure Checks",
    },
    miscellaneous: {
        name: "General Security Assessments",
    },
    misconfiguration: {
        name: "Configuration Integrity Review",
    },
    'default_logins': {
        name: "Authentication Security Evaluation ",
    },
    cves: {
        name: "Known Vulnerabilities Review",
    },
    'exposed_panels': {
        name: "Access Control Checks",
    },
    vulnerabilities: {
        name: "Targeted Threat Identification ",
    },
    fuzzing: {
        name: "Hidden Directories and Files Discovery",
    },
    crawling: {
        name: "Crawling",
    },
    crawling_additional_checks: {
        name: "Additional Checks",
    },
}

export const GPTTestConfigDefaults = {
    vulnerability: {
        rate_limit: 80,
        concurrency: 10,
        timeout: 30,
        retries: 2,
    },
    validation: {
        timeout: 30,
        retries: 2,
    },
};

// GenPT test-coverage options for API assets (ROAR-4426). `idor`/`comprehensive` initiate the IDOR flow.
export const GENPT_COVERAGE = {
    DAST: 'dast',
    COMPREHENSIVE: 'comprehensive',
    IDOR: 'idor',
};

// Coverage cards rendered on the API "Coverage" stepper step. Copy verbatim from the Figma IDOR-flow.
// Order matches the Figma reference: Comprehensive (recommended) → DAST → IDOR.
export const genptCoverageOptions = [
    {
        value: GENPT_COVERAGE.COMPREHENSIVE,
        title: "Comprehensive Security Coverage",
        description: "Covers all discovered endpoints for common vulnerabilities while also performing deeper authorization checks, including IDOR, on high-risk areas.",
        recommended: true,
        runsIdor: true,
    },
    {
        value: GENPT_COVERAGE.DAST,
        title: "DAST Analysis",
        description: "Tests endpoints to uncover general vulnerabilities like server misconfigurations and authentication flaws. Recommended for establishing a basic security posture.",
        recommended: false,
        runsIdor: false,
    },
    {
        value: GENPT_COVERAGE.IDOR,
        title: "IDOR Analysis",
        description: "Targets endpoints susceptible to IDOR to uncover unauthorized data exposure. Ideal for deeply validating user data isolation.",
        recommended: false,
        runsIdor: true,
    },
];

// True when a coverage selection should trigger the IDOR pre-declaration flow.
// Derived from the `runsIdor` flag on genptCoverageOptions so the cards stay the single source of truth.
export const coverageRunsIdor = (coverage) =>
    !!genptCoverageOptions.find((o) => o.value === coverage)?.runsIdor;

// Infer the coverage a prior session used from its persisted `test_type`. The backend does NOT store
// coverage separately (only `test_type`: 'idor' | 'comprehensive' | 'normal' | 'scenario'), so "Run
// last setup" reconstructs it here. Note: a Comprehensive run that found no IDOR candidates degrades
// to 'normal' server-side and is therefore indistinguishable from a plain DAST run — it replays as DAST.
//
// 'scenario' is a WebApp `genptWebAppCoverageOptions` value, not a `GENPT_COVERAGE` (API-asset) one —
// deliberately not added to that enum (see the "naming collision to avoid" note above) — so this
// returns the WebApp registry value itself rather than a GENPT_COVERAGE member. Without this case a
// scenario session would fall to `default: DAST` and "Run last setup" would silently replay it as a
// plain DAST run (plan D8, ROAR-4840).
export const coverageFromTestType = (testType) => {
    switch (testType) {
        case 'idor':
            return GENPT_COVERAGE.IDOR;
        case 'comprehensive':
            return GENPT_COVERAGE.COMPREHENSIVE;
        case 'scenario':
            return 'scenario';
        default:
            return GENPT_COVERAGE.DAST;
    }
};

// OWASP API Security Top 10 — radar axes shown alongside the coverage cards. Each axis carries its
// per-coverage `scores` (relative depth 0–100, keyed by GENPT_COVERAGE value), the single source of
// truth that drives the radar shape. IDOR concentrates on object-level auth (API-1/API-3); DAST
// spreads across misconfiguration/SSRF/auth; comprehensive is the union (deepest overall).
export const owaspApiTop10 = [
    { key: "API-1", label: "Broken Object Level Auth.", scores: { dast: 25, comprehensive: 95, idor: 100 } },
    { key: "API-2", label: "Broken Authentication", scores: { dast: 80, comprehensive: 85, idor: 30 } },
    { key: "API-3", label: "Broken Object Property Level Auth.", scores: { dast: 25, comprehensive: 90, idor: 95 } },
    { key: "API-4", label: "Unrestricted Resource Consumption", scores: { dast: 70, comprehensive: 75, idor: 20 } },
    { key: "API-5", label: "Broken Function Level Auth.", scores: { dast: 60, comprehensive: 70, idor: 20 } },
    { key: "API-6", label: "Unrestricted Access to Sensitive Business Flows", scores: { dast: 25, comprehensive: 25, idor: 30 } },
    { key: "API-7", label: "Server Side Request Forgery", scores: { dast: 85, comprehensive: 85, idor: 15 } },
    { key: "API-8", label: "Security Misconfiguration", scores: { dast: 90, comprehensive: 90, idor: 20 } },
    { key: "API-9", label: "Improper Inventory Management", scores: { dast: 70, comprehensive: 75, idor: 25 } },
    { key: "API-10", label: "Unsafe Consumption of APIs", scores: { dast: 65, comprehensive: 70, idor: 15 } },
];

// Derived coverage → { OWASP key: score } lookup for by-coverage access (radar values, tests).
export const owaspCoverageScores = Object.fromEntries(
    Object.values(GENPT_COVERAGE).map((c) => [
        c, Object.fromEntries(owaspApiTop10.map((a) => [a.key, a.scores[c] ?? 0])),
    ])
);


// Per-test IDOR result → colour token (mirrors genptTestStatus styling).
export const idorTestResultMap = {
    pending: { title: "Pending", bg_color: "var(--functional-blue-600)" },
    secure: { title: "Secure", bg_color: "var(--secondary-700)" },
    vulnerable: { title: "Vulnerable", bg_color: "var(--functional-red)" },
    skipped: { title: "Skipped", bg_color: "var(--neutral-400)" },
};

// GenPT test-coverage options for WebApp assets (ROAR-4839). Extracted verbatim from the two cards
// `TestTypeSection` used to hardcode, so the WebApp coverage set gets the same single source of
// truth `genptCoverageOptions` (above) already gives the API set. Keep the two lists separate:
// that one is the API-asset registry from the IDOR flow (ROAR-4426) and its `value`s are coverage
// ids, whereas these `value`s are `test_type` values that go on the wire.
//   `assetTypes`            — the `platform_category` values that may offer this option (see
//                             `utils/constants/assetTypeRegistry.js`), so a coverage card can be
//                             scoped to an asset type without another `if` in the component.
//                             WordPress is a `crm_type` on a `web_app` asset, not its own asset
//                             type (`TestSetup.js` reads it off `validateTargetData.crm_type`),
//                             so it does not appear in this list.
//   `forcesUnauthenticated` — picking this option drops the asset into unauthenticated mode.
export const genptWebAppCoverageOptions = [
  {
    value: 'quick',
    title: 'Express Test',
    tooltip:
      'Quicker scan focusing on the provided URL while maintaining essential vulnerability checks. Ideal for fast assessments and routine checks.',
    assetTypes: ['web_app'],
    forcesUnauthenticated: true,
  },
  {
    value: 'normal',
    title: 'Site-wide Test',
    tooltip:
      'Provides a more intensive security evaluation with increased depth and enhanced coverage of your web application. This thorough examination not only tests the provided URL but also analyzes all linked pages for complete vulnerability detection.',
    assetTypes: ['web_app'],
    forcesUnauthenticated: false,
  },
  {
    value: 'scenario',
    title: 'Business Scenario',
    tooltip:
      'Describe a specific business flow in plain language and GenPT tests only the endpoints that flow touches — a targeted alternative to scanning the entire site.',
    assetTypes: ['web_app'],
    forcesUnauthenticated: false,
  },
];

// The WebApp coverage options offerable for an asset type. A missing/falsy `assetType` falls back
// to every option, so a caller that hasn't wired the asset type through yet (e.g. this PR) can
// never blank the section. A recognized-but-unlisted type (e.g. `api`) DELIBERATELY yields `[]` —
// that's how AC1's "WebApp only" rule is enforced once a caller starts passing a real asset type.
export const webAppCoverageForAssetType = (assetType) =>
  assetType
    ? genptWebAppCoverageOptions.filter((o) => o.assetTypes.includes(assetType))
    : genptWebAppCoverageOptions;

// Business Scenario bounds, mirrored from the ROAR-4511 Cross-Repo Contract. api-backend and
// gpt-tools-api enforce the same numbers server-side; these drive the client-side counter and gate
// so a hopeless request never costs a round trip or one of the user's attempts.
export const SCENARIO_MIN_CHARS = 50;
export const SCENARIO_MAX_CHARS = 8000;
export const SCENARIO_MAX_ATTEMPTS = 5;
// Caps gpt-tools-api applies to its own model output; re-applied client-side by `sanitizeLlmText`.
export const SCENARIO_FEATURE_NAME_MAX_CHARS = 60;
export const SCENARIO_STEP_MAX_CHARS = 700;
// Matches gpt-tools-api's maxCandidateDescLen (500) and api-backend's
// CANDIDATE_DESCRIPTION_MAX_LENGTH so a 201-500 char sub-scenario description is not silently
// shortened in the picker (ROAR-4511 cross-repo reconciliation).
export const SCENARIO_CANDIDATE_DESCRIPTION_MAX_CHARS = 500;

// C0 and C1 control characters, including DEL. Stripped from LLM-authored text so a model cannot
// smuggle terminal escapes or line breaks into a chat bubble or a saved scenario name.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g;

/**
 * Normalise a string authored by an LLM before it is rendered or stored (ROAR-4839, plan D6).
 *
 * The portal is the LAST hop before a human reads `feature_name`, `steps[]` and navigation-step
 * descriptions. gpt-tools-api and api-backend each sanitise too; this is the third layer.
 * Control characters become spaces, whitespace runs collapse, and the result is capped and trimmed.
 *
 * This does NOT escape markup — it deliberately leaves `<` and `&` alone. Callers must render the
 * result as text and never through `dangerouslySetInnerHTML`.
 *
 * @param {*} value Candidate text; anything that is not a string yields `''`.
 * @param {number} [maxLength=SCENARIO_STEP_MAX_CHARS] Maximum length of the result.
 * @returns {string} Sanitised, capped, trimmed text.
 */
export const sanitizeLlmText = (value, maxLength = SCENARIO_STEP_MAX_CHARS) => {
  if (typeof value !== 'string') return '';
  return value.replace(CONTROL_CHARS, ' ').replace(/\s+/g, ' ').trim().slice(0, maxLength).trim();
};

